<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { Plus } from 'lucide-svelte';
  import { housekeepingApi } from '$lib/api/board';
  import { adminHousekeepingApi } from '$lib/api/admin';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import { session } from '$lib/state/session.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import StatusLamp from '$lib/components/ui/StatusLamp.svelte';
  import Toolbar from '$lib/components/ui/Toolbar.svelte';
  import DataTable from '$lib/components/ui/DataTable.svelte';
  import RowActions from '$lib/components/ui/RowActions.svelte';
  import type { HousekeepingItem, HousekeepingItemBody } from '$lib/types';

  const queryClient = useQueryClient();
  // hr only ever sees active items (GET /housekeeping, any authenticated
  // caller) — the full list including inactive ones is admin-only (GET
  // /admin/housekeeping), matching how Leave types splits view vs edit.
  const query = createQuery(() => ({
    queryKey: ['manage-housekeeping', session.isAdmin],
    queryFn: () => (session.isAdmin ? adminHousekeepingApi.list() : housekeepingApi.list())
  }));

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['manage-housekeeping'] });
    queryClient.invalidateQueries({ queryKey: ['housekeeping'] });
  }

  const emptyForm = (): HousekeepingItemBody => ({ title: '', active: true, sort: 0 });

  let editing = $state<HousekeepingItem | null>(null);
  let showForm = $state(false);
  let form = $state<HousekeepingItemBody>(emptyForm());
  let saving = $state(false);
  let error = $state('');

  function openCreate() {
    editing = null;
    form = emptyForm();
    error = '';
    showForm = true;
  }

  function openEdit(item: HousekeepingItem) {
    editing = item;
    form = { title: item.title, active: item.active, sort: item.sort };
    error = '';
    showForm = true;
  }

  async function save() {
    if (!form.title?.trim()) {
      error = 'Title is required.';
      return;
    }
    saving = true;
    error = '';
    try {
      if (editing) await adminHousekeepingApi.update(editing.id, form);
      else await adminHousekeepingApi.create(form);
      refresh();
      showForm = false;
      toast.success(editing ? 'Housekeeping item updated.' : 'Housekeeping item created.');
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      saving = false;
    }
  }

  async function toggleActive(item: HousekeepingItem) {
    const verb = item.active ? 'deactivate' : 'reactivate';
    if (!confirm(`${verb.charAt(0).toUpperCase() + verb.slice(1)} "${item.title}"?`)) return;
    try {
      await adminHousekeepingApi.update(item.id, { active: !item.active });
      refresh();
      toast.success(`Housekeeping item ${item.active ? 'deactivated' : 'reactivated'}.`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }
</script>

<div class="tym-page">
  <p class="tym-section-sub mb-4">
    Standing contributions employees can log against on a no-work day.
    {#if !session.isAdmin}You see active items only.{/if}
  </p>
  {#if session.isAdmin}
    <Toolbar>
      {#snippet actions()}
        <button type="button" class="btn-primary" onclick={openCreate}>
          <Plus class="h-4 w-4" /> New item
        </button>
      {/snippet}
    </Toolbar>
  {/if}

  {#if query.isLoading}
    <div class="tym-loading-state">Loading...</div>
  {:else if query.isError}
    <div class="tym-error-state">Could not load housekeeping items.</div>
  {:else if query.data}
    <DataTable
      mobileCards
      columns={[
        { key: 'title', label: 'Title', cell: titleCell },
        { key: 'sort', label: 'Sort', cell: sortCell },
        { key: 'active', label: 'Active', cell: activeCell },
        ...(session.isAdmin ? [{ key: 'actions', label: '', align: 'right' as const, cell: actionsCell }] : [])
      ]}
      rows={query.data}
      rowKey={(item) => item.id}
      emptyTitle="No housekeeping items yet"
      emptyBody={session.isAdmin ? 'Add one so employees have something to log against.' : 'None configured yet.'}
    />
  {/if}

  {#if showForm && session.isAdmin}
    <Modal title={editing ? 'Edit housekeeping item' : 'New housekeeping item'} onClose={() => (showForm = false)}>
      <div class="space-y-4">
        <div>
          <div class="label">Title</div>
          <input type="text" class="input" aria-label="Title" bind:value={form.title} />
        </div>
        <div>
          <div class="label">Sort order</div>
          <input type="number" class="input" aria-label="Sort order" bind:value={form.sort} />
        </div>
        <label class="flex items-center gap-2 text-[13px] text-toyama-heavy-carbon">
          <input type="checkbox" bind:checked={form.active} class="h-4 w-4 accent-toyama-teal" />
          Active
        </label>
        {#if error}<div class="tym-error-state">{error}</div>{/if}
        <div class="flex justify-end gap-2 pt-1">
          <button type="button" class="btn-secondary" onclick={() => (showForm = false)}>Cancel</button>
          <button type="button" class="btn-primary" disabled={saving} onclick={save}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  {/if}
</div>

{#snippet titleCell(item: HousekeepingItem)}
  <span class="font-medium text-navy">{item.title}</span>
{/snippet}
{#snippet sortCell(item: HousekeepingItem)}
  <span class="muted">{item.sort}</span>
{/snippet}
{#snippet activeCell(item: HousekeepingItem)}
  <span class="inline-flex items-center gap-1.5 {item.active ? 'text-navy' : 'text-dark-gray'}">
    <StatusLamp tone={item.active ? 'good' : 'idle'} />
    {item.active ? 'Active' : 'Inactive'}
  </span>
{/snippet}
{#snippet actionsCell(item: HousekeepingItem)}
  <RowActions
    items={[
      { label: 'Edit', onClick: () => openEdit(item) },
      { label: item.active ? 'Deactivate' : 'Reactivate', onClick: () => toggleActive(item), danger: item.active }
    ]}
  />
{/snippet}
